/**
 * Pure helpers for creating and inspecting the FormInputChild nodes of the
 * DataInput "Abfrage" form. Consolidates the three previously drifting copies
 * of the node blob (DataInputSettings.populateFieldsGroup, FieldsGroupSettings
 * count-change, DataInput participation injection) and the label/used-variable
 * derivations. No React, no craft — callers wrap the returned props in
 * `query.parseFreshNode({ data: { type: FormInput, displayName:
 * "FormInputChild", props } })`.
 */

import type { ParentFilter } from "../../utils/types/ParentFilter";
import { isPublicFormField } from "../../../settings/publicFormFields";

/** The participation ("Teilnahme") question variables — exactly one must exist. */
export const ACCEPTANCE_FIELD_VARIABLES = new Set(["state", "acceptanceState"]);

export const isAcceptanceFieldName = (fieldName: string): boolean =>
  ACCEPTANCE_FIELD_VARIABLES.has(fieldName);

export function makeAcceptanceVisibilityFilter(fieldName: string): ParentFilter {
  return {
    type: "&",
    simpleFilters: [],
    advancedFilters: [
      {
        type: "&",
        simpleFilters: [{ field: fieldName, operator: "=", value: "ACCEPTED" }],
      },
    ],
  };
}

/**
 * Does this node already carry a filter that actually governs anything?
 *
 * Mirrors the server's own reading (`evaluateVisibilityFilter` in
 * website-builder-backend `lib/form-field-visibility.ts`): ONLY
 * `advancedFilters` are evaluated, and a filter whose every entry has an empty
 * `simpleFilters` list resolves to "visible" — i.e. it governs nothing and is
 * indistinguishable from having no filter at all. A top-level `simpleFilters`
 * is ignored by the evaluator entirely, so it must not count here either.
 *
 * Used by the DataInput acceptance invariant to decide whether to stamp. That
 * invariant used to assign unconditionally, which was wrong twice over: it
 * dirtied the editor's undo history on every run (a fresh filter object is
 * never reference-equal, so Immer produced patches and the Save button lit up
 * by itself), and it overwrote COMPOSED filters — `injectRegistrationFields`
 * builds "acceptance AND the admin's own rows", and a blind re-stamp threw the
 * admin rows away.
 */
export function hasActiveVisibilityFilter(filter: ParentFilter | undefined | null): boolean {
  if (!filter) return false;
  return (filter.advancedFilters ?? []).some(
    (advanced) => (advanced?.simpleFilters ?? []).length > 0,
  );
}

/** Preferred auto-pick order when seeding a fresh fields group. */
export const MAIN_GUEST_DEFAULT_FIELD_ORDER = [
  "firstName",
  "lastName",
  "email",
  "state",
  "phone",
  "company",
  "jobTitle",
  "city",
  "country",
];

type FieldLike = {
  name: string;
  translation?: string;
  additionalField?: boolean;
  required?: boolean;
};

/**
 * Display label for a guest field: built-ins translate via the
 * `guest-fields` namespace; custom additional fields show their admin-defined
 * display label (`translation`), falling back to the immutable `name`
 * identifier for legacy fields.
 */
export function getGuestFieldLabel(
  fields: readonly FieldLike[],
  fieldName: string,
  tFields: (key: string) => string,
): string {
  const field = fields.find((f) => f.name === fieldName);
  if (!field) return fieldName;
  if (field.additionalField) return field.translation?.trim() || field.name;
  try {
    return tFields(field.name);
  } catch {
    return field.name;
  }
}

export type FieldEntry = {
  nodeId: string;
  props: { inputFieldVariable?: string; type?: string };
};

/**
 * Used main-guest variables across the DataInput's field groups. Companion
 * inputs don't count; `excludeNodeId` skips the field being edited itself.
 */
export function collectUsedFieldVariables(
  groups: readonly (readonly FieldEntry[])[],
  excludeNodeId?: string,
): Set<string> {
  const used = new Set<string>();
  for (const group of groups) {
    for (const entry of group) {
      if (excludeNodeId && entry.nodeId === excludeNodeId) continue;
      if (entry.props.type === "companion") continue;
      const variable = entry.props.inputFieldVariable;
      if (typeof variable === "string" && variable) used.add(variable);
    }
  }
  return used;
}

/**
 * Candidate variables for auto-seeding: the preferred rotation first (only
 * those that exist as event fields), then every remaining event field, deduped.
 *
 * The tail is filtered to PUBLIC-writeable variables. Without that, an event
 * with few custom fields exhausts the real candidates and the rotation reaches
 * `guestId`/`guestType`/`id` — auto-seeding a field that 400s every
 * submission, with no picker the editor could have refused it in.
 */
export function getCandidateFieldVariables(
  fields: readonly FieldLike[],
): string[] {
  return [
    ...MAIN_GUEST_DEFAULT_FIELD_ORDER.filter((name) =>
      fields.some((field) => field.name === name),
    ),
    ...fields.filter(isPublicFormField).map((field) => field.name),
  ].filter((fieldName, index, allNames) => allNames.indexOf(fieldName) === index);
}

export type FormInputChildProps = {
  label: string;
  inputFieldVariable: string;
  gridArea: string;
  isVisible: true;
  canDuplicate: false;
  border: true;
  borderStyle: "border-solid";
  borderWidth: "border";
  borderSides: [];
  fontSize: "text-sm";
  requiredField: boolean;
  changeable: true;
  locked: false;
  visibilityFilter?: ParentFilter;
};

/**
 * The canonical FormInputChild prop blob. The participation question and the
 * email field span the full row; every non-participation field is gated behind
 * the participation answer when `acceptanceFilter` is provided (matching what
 * the DataInput invariant would stamp anyway).
 */
export function buildFormInputProps(args: {
  fieldName: string;
  label: string;
  required?: boolean;
  acceptanceFilter?: ParentFilter;
}): FormInputChildProps {
  const { fieldName, label, required, acceptanceFilter } = args;
  const isAcceptanceField = isAcceptanceFieldName(fieldName);
  const shouldSpanFullWidth = fieldName === "email" || isAcceptanceField;

  return {
    label,
    inputFieldVariable: fieldName,
    gridArea: shouldSpanFullWidth ? "col-span-12" : "col-span-6",
    isVisible: true,
    canDuplicate: false,
    border: true,
    borderStyle: "border-solid",
    borderWidth: "border",
    borderSides: [],
    fontSize: "text-sm",
    requiredField: isAcceptanceField || (required ?? false),
    changeable: true,
    locked: false,
    ...(isAcceptanceField || !acceptanceFilter
      ? {}
      : { visibilityFilter: acceptanceFilter }),
  };
}
